import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { GuidedUiMode } from '@comp-sys/shared';

/**
 * 引导式 UI 运行时模式(第四维 uiMode,与皮肤正交)。
 * 默认 'classic'。是否允许 guided 由 admin 全局配置 guidedUiMode 决定(见 useGuidedUiMode),
 * DashboardLayout 的同步 effect 调 applyGuidedConfig 据配置收敛 uiMode。
 *
 * uiModeUserChosen:用户是否显式切换过(点过"体验新界面"/"切经典")。
 *  - true:尊重用户选择,applyGuidedConfig 在 button/default 模式下不覆盖(修:default 模式可退回经典)。
 *  - false:用模式默认(button→classic / default→guided)。
 *  - off/force 强制时重置为 false(强制模式不尊重用户选择)。
 * 持久化到 localStorage('comp-sys-ui-mode')。
 */
export type UiMode = 'classic' | 'guided';

interface UiModeState {
  uiMode: UiMode;
  uiModeUserChosen: boolean;
  /** 引导式首页一次性 intro coachmark 是否已看过。 */
  hasSeenIntro: boolean;
  /** 用户主动切换(经典 shell 体验新界面 / guided shell 切经典):设 uiMode + userChosen=true。 */
  setUiMode: (m: UiMode) => void;
  /** admin 全局配置收敛(dashboard 同步 effect 调用)。 */
  applyGuidedConfig: (g: GuidedUiMode) => void;
  setHasSeenIntro: () => void;
  reset: () => void;
}

export const useUiModeStore = create<UiModeState>()(
  persist(
    (set) => ({
      uiMode: 'classic',
      uiModeUserChosen: false,
      hasSeenIntro: false,
      setUiMode: (m) => set({ uiMode: m, uiModeUserChosen: true }),
      applyGuidedConfig: (g) =>
        set((s) => {
          if (g === 'off') return { uiMode: 'classic' as UiMode, uiModeUserChosen: false };
          if (g === 'force') return { uiMode: 'guided' as UiMode, uiModeUserChosen: false };
          // 'button' / 'default':尊重用户显式选择;否则用模式默认
          if (s.uiModeUserChosen) return {};
          const def: UiMode = g === 'default' ? 'guided' : 'classic';
          return s.uiMode === def ? {} : { uiMode: def };
        }),
      setHasSeenIntro: () => set({ hasSeenIntro: true }),
      reset: () => set({ uiMode: 'classic', uiModeUserChosen: false, hasSeenIntro: false }),
    }),
    {
      name: 'comp-sys-ui-mode',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        uiMode: s.uiMode,
        uiModeUserChosen: s.uiModeUserChosen,
        hasSeenIntro: s.hasSeenIntro,
      }),
    },
  ),
);
